'use client';

import { useMemo, useRef, useState } from 'react';

import Image from 'next/image';

import { Button, PageHeader, RelatedContent } from '@paction-2.0/ui';
import { ChipField, ShareLinks } from '@paction-2.0/ui/client';
import {
  DEFAULT_PAGE_COPY,
  PET_LOSS_RESPONSES_FLAG,
  defaultQuestionsFor,
  type PetLossLocale
} from '@paction-2.0/util/petLossReadiness';
import { PACTION_PUBLIC_ORIGIN } from '@paction-2.0/util/url';

import { PublicPageShell } from '@/components/site/public/PublicPageShell';
import { READINESS_UI_COPY } from '@/features/petLossReadiness/copy';
import {
  RELATED_CONTENT,
  outcomeFor
} from '@/features/petLossReadiness/petLossReadiness.constants';
import { PET_LOSS_READINESS_PATHS } from '@/features/petLossReadiness/paths';
import { ReadinessConsent } from '@/features/petLossReadiness/ReadinessConsent';
import { ReadinessIntro } from '@/features/petLossReadiness/ReadinessIntro';
import { saveReadinessResponse } from '@/features/petLossReadiness/saveReadinessResponse';
import { useReadinessAnswers } from '@/features/petLossReadiness/useReadinessAnswers';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';

import {
  TimingIcon,
  EmotionsIcon,
  MotivationIcon,
  HouseholdIcon,
  NextStepsIcon
} from '@/features/petLossReadiness/StepIcons';
import styles from '@/features/petLossReadiness/petLossReadinessPage.module.scss';

const STEP_ICONS = [
  TimingIcon,
  EmotionsIcon,
  MotivationIcon,
  HouseholdIcon,
  NextStepsIcon
];

const LANGUAGE_OPTIONS: Array<{ locale: PetLossLocale; label: string }> = [
  { locale: 'fr', label: 'Français' },
  { locale: 'en', label: 'English' }
];

const LANGUAGE_GROUP_LABEL: Record<PetLossLocale, string> = {
  en: 'Language',
  fr: 'Langue'
};

function LanguageToggle({
  locale,
  onChange
}: {
  locale: PetLossLocale;
  onChange: (locale: PetLossLocale) => void;
}) {
  return (
    <div
      className={styles.languageToggle}
      role="group"
      aria-label={LANGUAGE_GROUP_LABEL[locale]}
    >
      {LANGUAGE_OPTIONS.map((option) => (
        <button
          key={option.locale}
          type="button"
          lang={option.locale}
          className={styles.languageOption}
          aria-pressed={option.locale === locale}
          onClick={() => onChange(option.locale)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ResultsCallout({
  title,
  text,
  logoSrc,
  links
}: {
  title: string;
  text: string;
  logoSrc?: string;
  links: Array<{ label: string; href: string; external?: boolean }>;
}) {
  return (
    <div className={styles.callout}>
      <h3 className={styles.calloutTitle}>{title}</h3>
      <p className={styles.calloutText}>{text}</p>
      {logoSrc && (
        <div className={styles.calloutLogo}>
          <img src={logoSrc} alt="APLB Logo" />
        </div>
      )}
      <div className={styles.calloutLinks}>
        {links.map((link) => (
          <a
            key={`${link.href}-${link.label}`}
            className={styles.calloutLink}
            href={link.href}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noopener noreferrer' : undefined}
          >
            {link.label} →
          </a>
        ))}
      </div>
    </div>
  );
}

export function PetLossReadinessClient({
  initialLocale = 'en'
}: {
  initialLocale?: PetLossLocale;
}) {
  const [locale, setLocale] = useState<PetLossLocale>(initialLocale);
  const page = DEFAULT_PAGE_COPY[locale];
  const copy = READINESS_UI_COPY[locale];
  const questions = useMemo(() => defaultQuestionsFor(locale), [locale]);
  const quizRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [shareAnswers, setShareAnswers] = useState(false);
  const collectResponses = useFeatureFlag(PET_LOSS_RESPONSES_FLAG);

  const {
    answers,
    handleSelect,
    handleSubmit,
    handleRestart,
    isComplete,
    showResults,
    outcomeKey
  } = useReadinessAnswers(questions);
  const outcome = outcomeKey ? outcomeFor(outcomeKey, locale) : null;

  const onLocaleChange = (next: PetLossLocale) => {
    if (next === locale) return;
    setLocale(next);
    window.history.replaceState(null, '', PET_LOSS_READINESS_PATHS[next]);
  };

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onBegin = () => {
    setShowQuiz(true);
    setTimeout(() => scrollTo(quizRef), 100);
  };

  const onSubmit = () => {
    if (!handleSubmit()) return;
    if (collectResponses && shareAnswers) {
      void saveReadinessResponse({ answers });
    }
    setTimeout(() => scrollTo(resultsRef), 100);
  };

  const onRestart = () => {
    handleRestart();
    setShareAnswers(false);
    setShowQuiz(false);
  };

  return (
    <>
      <PageHeader
        title={page.pageTitle}
        logoSrc="/icons/rainbow-icon.svg"
        logoAlt="pet loss support"
      />
      <PublicPageShell maxWidth="default" className={styles.pageWrapper}>
        <div lang={locale} className={styles.localeRoot}>
          <LanguageToggle locale={locale} onChange={onLocaleChange} />
          <ReadinessIntro locale={locale} />

          {/* Get-started section */}
          {!showResults && (
            <>
              <div className={styles.card}>
                <div className={styles.getStartedGrid}>
                  <div className={styles.getStartedInputs}>
                    <div>
                      <div className={styles.getStartedCopyTitle}>
                        {page.introHeading}
                      </div>
                      <p className={styles.getStartedCopy}>{page.introText}</p>
                    </div>
                    <div className={styles.beginButtonWrap}>
                      <Button
                        type="button"
                        className={styles.beginButton}
                        onClick={onBegin}
                      >
                        {copy.begin}
                      </Button>
                    </div>
                  </div>
                  <div className={styles.getStartedImageWrap}>
                    <Image
                      className={styles.getStartedImageIcon}
                      src={page.introImageUrl}
                      alt=""
                      width={480}
                      height={320}
                      unoptimized
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* All questions on one page */}
          {showQuiz && !showResults && (
            <div className={`${styles.card} ${styles.stacked}`} ref={quizRef}>
              <section
                className={styles.stepsSection}
                aria-label={page.stepsHeading}
              >
                <h2 className={styles.stepsHeading}>{page.stepsHeading}</h2>
                <div className={styles.stepsGrid}>
                  {STEP_ICONS.map((Icon, index) => (
                    <div key={index} className={styles.stepCard}>
                      <span className={styles.stepIcon}>
                        <Icon />
                      </span>
                      <span className={styles.stepLabel}>
                        {page.stepLabels[index]}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <div className={styles.quiz}>
                {questions.map((question) => (
                  <ChipField
                    key={question.id}
                    label={question.prompt}
                    headingLevel={2}
                    options={question.options.map((opt) => ({
                      value: opt.value,
                      label: opt.label
                    }))}
                    selectedOptions={answers[question.id] ?? ''}
                    onChange={(value) => {
                      if (typeof value === 'string')
                        handleSelect(question.id, value);
                    }}
                  />
                ))}

                {collectResponses && (
                  <ReadinessConsent
                    checked={shareAnswers}
                    onChange={setShareAnswers}
                    label={copy.consentLabel}
                    description={copy.consentDescription}
                  />
                )}

                <div className={styles.submitSection}>
                  <Button
                    type="button"
                    onClick={onSubmit}
                    disabled={!isComplete}
                    className={styles.submitButton}
                  >
                    {copy.seeResult}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {showResults && outcome && (
            <div
              className={`${styles.card} ${styles.stacked} ${styles[outcome.key]}`}
              ref={resultsRef}
            >
              <div className={styles.results}>
                <span className={styles.resultBadge}>{outcome.badge}</span>
                <h2 className={styles.resultHeading}>{outcome.heading}</h2>
                <p className={styles.resultBody}>{outcome.body}</p>

                {outcome.callout && (
                  <ResultsCallout
                    title={outcome.callout.title}
                    text={outcome.callout.text}
                    logoSrc={outcome.callout.logoSrc}
                    links={outcome.callout.links}
                  />
                )}

                <div className={styles.resultNext}>
                  <RelatedContent
                    title={page.whatsNextTitle}
                    items={outcome.whatsNext}
                    headingLevel="h3"
                  />
                </div>

                <div className={styles.disclaimer}>{page.disclaimer}</div>

                <div className={styles.resultActions}>
                  <Button type="button" variant="secondary" onClick={onRestart}>
                    {copy.startOver}
                  </Button>
                </div>

                <div className={styles.share}>
                  <ShareLinks
                    link={`${PACTION_PUBLIC_ORIGIN}${PET_LOSS_READINESS_PATHS[locale]}`}
                    onlyCopy
                  />
                </div>
              </div>
            </div>
          )}

          <RelatedContent
            wide
            items={RELATED_CONTENT}
            className={styles.related}
          />
        </div>
      </PublicPageShell>
    </>
  );
}
